import React, {FC, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {
    CheckboxElement,
    DropdownElement,
    EnumDropdownElement,
    ItemsTableElement,
    MultilineTextElement,
    TextFieldElement
} from "@ib-tehnologije/react-widgets";
import {RenderAntdGraphQLList} from "../../../Components/AdminModalForm";
import {PageHeader, Panel, StatusTag} from "../../../Components/ui";
import {
    OptionPriceEffect,
    OptionQuestionType,
    useCreateEventOptionMutation,
    useEventOptionsForConfigQuery,
    useUpdateEventOptionMutation
} from "../../../GraphQL/generated/graphql";
import {humanizeEnumValue} from "../../../i18n/labelHelpers";
import {formatLocalDate} from "../adminDateFormat";
import {
    buildEventOptionCreateDto,
    buildEventOptionUpdateDto,
    createDefaultEventOptionFormData,
    formatEventOptionChoiceRows,
    isEventOptionCharged,
    normalizeSupportedEventOptionQuestionType
} from "../eventOptionFormPayload";
import {dateInputValue, formBooleanValue, isChoiceActivityQuestionType} from "./eventConfigPayloads";
import {
    EventOptionConfigRow
} from "./eventConfigQueries";
import {useEventConfigScope, useEventFeeTypesQuery} from "./useEventConfigScope";
import "./eventConfig.css";

const supportedEventOptionQuestionTypes = {
    YesNo: OptionQuestionType.YesNo,
    SingleChoice: OptionQuestionType.SingleChoice,
    MultiChoice: OptionQuestionType.MultiChoice,
    Text: OptionQuestionType.Text
};

const hasActivityVisibilityDependencySource = (value: unknown) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0;
};

// Charged activities are always yes/no questions.
const eventOptionQuestionTypeFormDefinition = {
    translationKey: "question-type",
    type: new EnumDropdownElement({enumData: supportedEventOptionQuestionTypes, showCancel: false}),
    disabledIf: (formikProps: any) => formBooleanValue(formikProps?.values?.isCharged, true),
    dependsOn: [{
        key: "isCharged",
        onChanged: async (_data: any, value: unknown) => formBooleanValue(value)
            ? [{
                eventType: "updateValueAndStorage",
                payload: {
                    formKey: "questionType",
                    value: OptionQuestionType.YesNo,
                    storage: {},
                    stopPropagation: true
                }
            }]
            : undefined
    }]
};

const eventOptionPriceFormDefinition = {
    translationKey: "price",
    type: new TextFieldElement({}),
    hiddenIf: (formikProps: any) => !formBooleanValue(formikProps?.values?.isCharged, true),
    dependsOn: [{
        key: "isCharged",
        onChanged: async (data: any, value: unknown) => {
            if (formBooleanValue(value)) {
                return undefined;
            }

            if (data && typeof data === "object") {
                data.price = 0;
            }

            return [{
                eventType: "updateValueAndStorage",
                payload: {
                    formKey: "price",
                    value: 0,
                    storage: {},
                    stopPropagation: true
                }
            }];
        }
    }]
};

const eventOptionChoicesFormDefinition = {
    translationKey: "choices",
    type: new ItemsTableElement({
        columns: {
            labelHr: {translationKey: "label-hr", type: new TextFieldElement({})},
            labelEn: {translationKey: "label-en", type: new TextFieldElement({})}
        }
    }),
    hiddenIf: (formikProps: any) => !isChoiceActivityQuestionType(formikProps?.values?.questionType)
};

// Event workspace "activities": selectable paid or free add-ons.
const EventActivitiesPage: FC = () => {
    const {t} = useTranslation();
    const scope = useEventConfigScope();
    const {eventNumericId, baseWhere} = scope;
    const selectedEventIdForSetup = eventNumericId ?? 0;
    const optionsQuery = useEventOptionsForConfigQuery({
        variables: {take: 50, where: baseWhere()}
    });
    const feeTypesQuery = useEventFeeTypesQuery(scope);
    const [createOption] = useCreateEventOptionMutation();
    const [updateOption] = useUpdateEventOptionMutation();

    const boolOptions = useMemo(() => [
        {id: true, label: t("yes", {defaultValue: "Yes"})},
        {id: false, label: t("no", {defaultValue: "No"})}
    ], [t]);
    const eventOptionNumberValidationLabels = useMemo(() => ({
        price: t("price", {defaultValue: "Price"}),
        requiredMessage: (field: string) => t("validation.field-required", {
            defaultValue: "{{field}} is required.",
            field
        }),
        numberMessage: (field: string) => t("validation.number", {
            defaultValue: "{{field}} must be a number.",
            field
        }),
        choiceLabelHr: t("label-hr", {defaultValue: "Label (Croatian)"}),
        choiceLabelEn: t("label-en", {defaultValue: "Label (English)"})
    }), [t]);
    const feeTypeOptions = useMemo(
        () => (feeTypesQuery.data?.eventFeeTypes?.items ?? []).map(ft => ({id: ft.id, label: ft.labelHr ?? ft.labelEn})),
        [feeTypesQuery.data?.eventFeeTypes?.items]
    );
    const feeTypeLabelsById = useMemo(() => {
        const map = new Map<number, string>();
        (feeTypesQuery.data?.eventFeeTypes?.items ?? []).forEach(ft => {
            if (ft) {
                map.set(ft.id, ft.labelHr ?? ft.labelEn ?? "");
            }
        });
        return map;
    }, [feeTypesQuery.data?.eventFeeTypes?.items]);
    const eventOptionLabelsById = useMemo(() => {
        const map = new Map<number, string>();
        (optionsQuery.data?.eventOptions?.items ?? []).forEach(option => {
            if (option) {
                map.set(option.id, option.labelHr ?? option.labelEn ?? "");
            }
        });
        return map;
    }, [optionsQuery.data?.eventOptions?.items]);
    const yesNoEventOptionOptions = useMemo(
        () => (optionsQuery.data?.eventOptions?.items ?? [])
            .filter((option): option is EventOptionConfigRow =>
                !!option &&
                option.questionType === OptionQuestionType.YesNo &&
                option.isActive !== false)
            .map(option => ({id: option.id, label: option.labelHr ?? option.labelEn ?? ""})),
        [optionsQuery.data?.eventOptions?.items]
    );

    const formDefinition = useMemo(() => ({
        labelHr: {translationKey: "label-hr", type: new TextFieldElement({})},
        labelEn: {translationKey: "label-en", type: new TextFieldElement({})},
        descriptionHr: {translationKey: "description-hr", type: new MultilineTextElement({rows: 2})},
        descriptionEn: {translationKey: "description-en", type: new MultilineTextElement({rows: 2})},
        isCharged: {translationKey: "event-option-is-charged", type: new CheckboxElement({})},
        tracksUsageOrAttendance: {translationKey: "activity-tracks-usage-or-attendance", type: new CheckboxElement({})},
        allowedFeeTypes: {
            translationKey: "activity-allowed-fee-types",
            type: new ItemsTableElement({
                columns: {
                    feeTypeId: {
                        translationKey: "fee-type",
                        type: new DropdownElement({
                            values: feeTypeOptions,
                            valueKey: "id",
                            titleKey: "label",
                            showEmpty: false
                        })
                    }
                }
            })
        },
        visibilityDependsOnEventOptionId: {
            translationKey: "activity-visible-after-activity",
            type: new DropdownElement({
                values: yesNoEventOptionOptions,
                valueKey: "id",
                titleKey: "label",
                showEmpty: true
            })
        },
        visibilityDependsOnExpectedAnswer: {
            translationKey: "activity-visible-after-answer",
            type: new DropdownElement({values: boolOptions, valueKey: "id", titleKey: "label", showEmpty: false}),
            hiddenIf: (formikProps: any) => !hasActivityVisibilityDependencySource(formikProps?.values?.visibilityDependsOnEventOptionId),
            dependsOn: [{
                key: "visibilityDependsOnEventOptionId",
                onChanged: async (data: any, value: unknown) => {
                    const nextValue = hasActivityVisibilityDependencySource(value) ? true : null;

                    if (data && typeof data === "object") {
                        data.visibilityDependsOnExpectedAnswer = nextValue;
                    }

                    return [{
                        eventType: "updateValueAndStorage",
                        payload: {
                            formKey: "visibilityDependsOnExpectedAnswer",
                            value: nextValue,
                            storage: {},
                            stopPropagation: true
                        }
                    }];
                }
            }]
        },
        questionType: eventOptionQuestionTypeFormDefinition,
        choices: eventOptionChoicesFormDefinition,
        price: eventOptionPriceFormDefinition,
        publicRegistrationEndDate: {
            translationKey: "activity-public-registration-end-date",
            type: new TextFieldElement({inputProps: {type: "date"}})
        },
        isActive: {translationKey: "active", type: new DropdownElement({values: boolOptions, valueKey: "id", titleKey: "label", showEmpty: false})}
    }), [boolOptions, feeTypeOptions, yesNoEventOptionOptions]);

    return (
        <div className="ati-stack">
            <PageHeader level={2} title={t("setup-sequence.activities-title", {defaultValue: "Activities/add-ons"})}/>
            <Panel className="event-config-activities">
                <RenderAntdGraphQLList
                    tableData={optionsQuery.data?.eventOptions}
                    columns={[
                        {columnId: "labelHr", title: t("label-hr"), sorter: true, renderCell: r => r.labelHr},
                        {columnId: "labelEn", title: t("label-en"), sorter: true, renderCell: r => r.labelEn},
                        {columnId: "questionType", title: t("question-type", {defaultValue: "Question type"}), sorter: true, renderCell: r => humanizeEnumValue(r.questionType)},
                        {
                            columnId: "priceEffect",
                            title: t("event-option-is-charged", {defaultValue: "Is activity charged?"}),
                            sorter: true,
                            renderCell: r => isEventOptionCharged(r.priceEffect) ? t("yes") : t("no")
                        },
                        {
                            columnId: "tracksUsageOrAttendance",
                            title: t("activity-tracks-usage-or-attendance", {defaultValue: "Track utilization/attendance?"}),
                            sorter: true,
                            renderCell: r => r.tracksUsageOrAttendance ? t("yes") : t("no")
                        },
                        {
                            columnId: "allowedFeeTypeIds",
                            title: t("activity-allowed-fee-types", {defaultValue: "Allowed participant fee types"}),
                            sorter: true,
                            renderCell: r => (r.allowedFeeTypeIds ?? []).length === 0
                                ? t("all", {defaultValue: "All"})
                                : (r.allowedFeeTypeIds ?? [])
                                    .map((feeTypeId: number) => feeTypeLabelsById.get(feeTypeId) ?? feeTypeId)
                                    .join(", ")
                        },
                        {
                            columnId: "visibilityDependsOnEventOptionId",
                            title: t("activity-visibility-rule", {defaultValue: "Visibility rule"}),
                            sorter: true,
                            renderCell: r => r.visibilityDependsOnEventOptionId
                                ? `${eventOptionLabelsById.get(r.visibilityDependsOnEventOptionId) ?? r.visibilityDependsOnEventOptionId}: ${r.visibilityDependsOnExpectedAnswer ? t("yes") : t("no")}`
                                : t("all", {defaultValue: "All"})
                        },
                        {columnId: "price", title: t("price"), sorter: true, renderCell: r => r.price},
                        {
                            columnId: "publicRegistrationEndDate",
                            title: t("activity-public-registration-end-date", {defaultValue: "Activity registration end date"}),
                            sorter: true,
                            renderCell: r => formatLocalDate(r.publicRegistrationEndDate)
                        },
                        {
                            columnId: "isActive",
                            title: t("active"),
                            sorter: true,
                            renderCell: r => r.isActive
                                ? <StatusTag tone="success">{t("yes")}</StatusTag>
                                : <StatusTag tone="neutral">{t("no")}</StatusTag>
                        }
                    ]}
                    creating={{
                        fn: (options: any) => createOption(options as any),
                        modelKey: "input",
                        baseFormData: createDefaultEventOptionFormData(selectedEventIdForSetup),
                        formDefinition,
                        formDataToDto: values => buildEventOptionCreateDto(
                            values,
                            selectedEventIdForSetup,
                            eventOptionNumberValidationLabels
                        )
                    }}
                    updating={{
                        fn: (options: any) => updateOption(options as any),
                        modelKey: "input",
                        formDefinition,
                        formDataToDto: values => buildEventOptionUpdateDto(
                            values,
                            selectedEventIdForSetup,
                            eventOptionNumberValidationLabels
                        ),
                        selectionToBaseFormData: item => {
                            const isCharged = isEventOptionCharged(item?.priceEffect ?? OptionPriceEffect.AddLineItem);

                            return {
                                ...createDefaultEventOptionFormData(item?.eventId ?? selectedEventIdForSetup),
                                id: item?.id,
                                eventId: item?.eventId ?? selectedEventIdForSetup,
                                labelHr: item?.labelHr ?? "",
                                labelEn: item?.labelEn ?? "",
                                descriptionHr: item?.descriptionHr ?? "",
                                descriptionEn: item?.descriptionEn ?? "",
                                vatRate: 0,
                                isCharged,
                                tracksUsageOrAttendance: item?.tracksUsageOrAttendance ?? false,
                                allowedFeeTypes: (item?.allowedFeeTypeIds ?? []).map((feeTypeId: number) => ({feeTypeId})),
                                visibilityDependsOnEventOptionId: item?.visibilityDependsOnEventOptionId ?? null,
                                visibilityDependsOnExpectedAnswer: item?.visibilityDependsOnExpectedAnswer ?? null,
                                questionType: isCharged
                                    ? OptionQuestionType.YesNo
                                    : normalizeSupportedEventOptionQuestionType(item?.questionType),
                                choices: formatEventOptionChoiceRows(item?.choices ?? []),
                                price: item?.price ?? 0,
                                publicRegistrationEndDate: dateInputValue(item?.publicRegistrationEndDate),
                                isActive: item?.isActive ?? true
                            };
                        }
                    }}
                    loading={optionsQuery.loading}
                    getRowId={row => row.id}
                    getNotificationKey={row => `${row.labelHr ?? row.labelEn}`}
                    query={optionsQuery}
                    modalWidth={960}
                    modalFormLayout={"paired-bilingual"}
                    pageStorageKey={"event-options"}
                />
            </Panel>
        </div>
    );
};

export default EventActivitiesPage;
